import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  TAIRecommendation,
  TProductInteraction,
  TSetProductInteraction,
  TWidgetIframeParentDimension,
} from '@lib/core/service/types/interface';

export interface IWidgetInstance {
  pageScrollValue: string | null;
  widgetIframeParentDimension: TWidgetIframeParentDimension;
  tasteMatchProductId: string | null;
  productInteractionVariant: TProductInteraction;
  aiRecommendation: TAIRecommendation | null;
  isBlockerLauncherShown: boolean;
  isRedirectToAILauncher: boolean;
}

const initialProductInteraction: TProductInteraction = {
  index: 0,
  isShow: false,
  productId: null,
  timer: null,
};

export const initialState: IWidgetInstance = {
  aiRecommendation: null,
  isBlockerLauncherShown: false,
  isRedirectToAILauncher: false,
  pageScrollValue: null,
  productInteractionVariant: initialProductInteraction,
  tasteMatchProductId: null,
  widgetIframeParentDimension: null,
};

export const widgetInstanceSlice = createSlice({
  initialState,
  name: 'widgetInstance',
  reducers: {
    resetAIRecommendation(state) {
      state.aiRecommendation = null;
    },
    resetProductInteraction(state) {
      state.productInteractionVariant = initialProductInteraction;
    },
    setAIRecommendation: (state, { payload }: PayloadAction<TAIRecommendation>) => {
      state.aiRecommendation = payload;
    },
    setIsBlockerLauncherShown: (state, { payload }: PayloadAction<boolean>) => {
      state.isBlockerLauncherShown = payload;
    },
    setIsRedirectToAILauncher: (state, { payload }: PayloadAction<boolean>) => {
      state.isRedirectToAILauncher = payload;
    },
    setPageScrollValue: (state, { payload }: PayloadAction<{ scrollAction: string }>) => {
      const { scrollAction } = payload;
      state.pageScrollValue = scrollAction;
    },
    setProductInteraction: (state, { payload }: PayloadAction<TSetProductInteraction>) => {
      const { index, productId, timer, toggleIsShow } = payload;

      const currentProductInteraction = state.productInteractionVariant ?? initialProductInteraction;

      let nextProductId = currentProductInteraction.productId;
      if (typeof productId !== 'undefined') {
        nextProductId = productId === null || productId === currentProductInteraction.productId ? null : productId;
      }

      let nextTimer = currentProductInteraction.timer;
      if (typeof timer !== 'undefined') {
        nextTimer = timer ?? null;
      }

      let nextIndex = currentProductInteraction.index;
      if (typeof index !== 'undefined') {
        nextIndex = (currentProductInteraction.index + 1) % index;
      }

      let nextIsShow = currentProductInteraction.isShow;
      if (toggleIsShow) {
        nextIsShow = !currentProductInteraction.isShow;
      }

      state.productInteractionVariant = {
        index: nextIndex,
        isShow: nextIsShow,
        productId: nextProductId,
        timer: nextTimer,
      };
    },
    setTasteMatchProductId: (state, { payload }) => {
      state.tasteMatchProductId = payload;
    },
    setWidgetIframeParentDimension(state, { payload }: PayloadAction<TWidgetIframeParentDimension>) {
      state.widgetIframeParentDimension = payload;
    },
  },
});

export default widgetInstanceSlice.reducer;

export const {
  setProductInteraction,
  setWidgetIframeParentDimension,
  setPageScrollValue,
  resetProductInteraction,
  setTasteMatchProductId,
  setAIRecommendation,
  resetAIRecommendation,
  setIsBlockerLauncherShown,
  setIsRedirectToAILauncher,
} = widgetInstanceSlice.actions;
